import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import {
  MwButton,
  MwInputText,
  MwModal,
  MwSpinnerButton,
} from "../components/ui";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import SelectorTree from "../components/ui/SelectAccount/SelectorTree";
import AccountContext from "../utils/AccountsHelper";
import BalanceContext from "../utils/BalanceHalper";
import { useTenant } from "../hooks";

const intialState = {
  id: "",
  fin_year_id: "",
  account_no: "",
  account_label: "",
  account_nat: "",
  cr: "",
  de: "",
};
const FormReducer = (state, action) => {
  switch (action.type) {
    case "fin_year_id":
      return { ...state, fin_year_id: action.value };
    case "account_no":
      return { ...state, account_no: action.value };
    case "account_label":
      return { ...state, account_label: action.value };
    case "account_nat":
      return { ...state, account_nat: action.value };

    case "cr":
      return { ...state, cr: action.value };
    case "de":
      return { ...state, de: action.value };
    case "id":
      return { ...state, id: action.value };
    case "reset":
      return { ...intialState };
    default:
      return state;
  }
};

export const BalancePopup = ({
  isOpen = false,
  setIsOpen = () => {},
  onClose = () => {},
  actionData = { mode: null, balanceId: null, balances: null },
  loading,
}) => {
  const [t] = useTranslation("global");
  const { tenantId, branchSelected, companySelected } = useTenant();
  const periodId = JSON.parse(
    localStorage.getItem("selectedPeriod") || "{}",
  ).id;
  const [formData, dispatch] = useReducer(FormReducer, intialState);
  const { accounts } = useContext(AccountContext);
  const { addBalance, editBalance, balances: existingBalances } =
    useContext(BalanceContext);
  const [errors, setErrors] = useState({});
  const handleClose = () => {
    dispatch({ type: "reset" });
    setIsOpen(false);
    if (onClose) onClose();
  };
  const findAccountRecursive = (accounts, accountNo) => {
    for (const acc of accounts) {
      if (acc.account_no === accountNo) return acc;

      if (acc.childs?.length) {
        const found = findAccountRecursive(acc.childs, accountNo);
        if (found) return found;
      }
    }
    return null;
  };
  const accountNo = actionData?.balances?.account_no;
  const account = findAccountRecursive(accounts, accountNo);

  const accountName =
    i18next.language === "ar"
      ? account?.name_ar || "-"
      : account?.name_en || "-";
  const resolveAccountMeta = (
    selectedAccountNo,
    accountData = null,
    { preserveOnMissing = false } = {},
  ) => {
    const target =
      accountData || findAccountRecursive(accounts, selectedAccountNo);
    if (!target) {
      if (!preserveOnMissing) {
        dispatch({ type: "account_label", value: "" });
        dispatch({ type: "account_nat", value: "" });
      }
      return;
    }
    const label =
      i18next.language === "ar"
        ? target?.name_ar || target?.name_en || ""
        : target?.name_en || target?.name_ar || "";
    const nature =
      target?.nat_account ||
      target?.accountNature ||
      target?.accounts_tree_relation?.nat_account ||
      "";
    dispatch({ type: "account_label", value: label });
    dispatch({ type: "account_nat", value: nature });
  };
  const accountNature = String(formData.account_nat || "")
    .trim()
    .toLowerCase();
  const isDebitNature = accountNature === "debit" || accountNature === "dr";
  const isCreditNature =
    accountNature === "cred" ||
    accountNature === "credit" ||
    accountNature === "cr";
  const isEditMode = actionData?.mode === "EDIT";
  const blockedAccountNumbers = useMemo(() => {
    const blocked = new Set();
    (Array.isArray(existingBalances) ? existingBalances : []).forEach(
      (balance) => {
        const acc = String(balance?.account_no || "").trim();
        if (acc) blocked.add(acc);
      },
    );
    const current = String(formData.account_no || "").trim();
    if (current) blocked.add(current);
    return Array.from(blocked);
  }, [existingBalances, formData.account_no]);

  useEffect(() => {
    if (actionData?.mode === "EDIT" && actionData?.balances) {
      const balance = actionData.balances;
      dispatch({ type: "id", value: balance.id });
      dispatch({ type: "account_no", value: balance.account_no });
      dispatch({
        type: "account_nat",
        value: balance.accounts_tree_relation?.nat_account,
      });
      dispatch({
        type: "account_label",
        value: accountName && accountName !== "-" ? accountName : "",
      });
      dispatch({ type: "cr", value: balance.cr });
      dispatch({ type: "de", value: balance.de });
      resolveAccountMeta(balance.account_no, null, { preserveOnMissing: true });
    }

    if (actionData?.mode === "ADD") {
      dispatch({ type: "reset" });
      dispatch({
        type: "account_nat",
        value: "",
      });
      dispatch({
        type: "account_label",
        value: "",
      });
    }
  }, [actionData]);
  useEffect(() => {
    if (!formData.account_no) return;
    if (!Array.isArray(accounts) || accounts.length === 0) return;
    resolveAccountMeta(formData.account_no, null, { preserveOnMissing: true });
  }, [accounts, formData.account_no]);
  useEffect(() => {
    if (isDebitNature && formData.cr) {
      dispatch({ type: "cr", value: "" });
    }
    if (isCreditNature && formData.de) {
      dispatch({ type: "de", value: "" });
    }
  }, [isDebitNature, isCreditNature, formData.cr, formData.de]);
  const validation = () => {
    const newErrors = {};

    if (!formData.account_no) {
      newErrors.account_no = t("openingBalance.accountRequired");
    }

    const hasValue = (value) => String(value ?? "").trim() !== "";
    const hasCr = hasValue(formData.cr);
    const hasDe = hasValue(formData.de);

    if (isDebitNature) {
      if (!hasDe) newErrors.de = t("openingBalance.oneRequired");
      if (hasCr) newErrors.cr = t("openingBalance.invalidNature");
    } else if (isCreditNature) {
      if (!hasCr) newErrors.cr = t("openingBalance.oneRequired");
      if (hasDe) newErrors.de = t("openingBalance.invalidNature");
    } else {
      if (!hasCr && !hasDe) {
        newErrors.cr = t("openingBalance.oneRequired");
        newErrors.de = t("openingBalance.oneRequired");
      }

      if (hasCr && hasDe) {
        newErrors.cr = t("openingBalance.onlyOneAllowed");
        newErrors.de = t("openingBalance.onlyOneAllowed");
      }
    }
    const balances = Array.isArray(actionData?.balances)
      ? actionData.balances
      : [];

    const isDuplicate = balances.some(
      (balance) =>
        String(balance.account_no) === String(formData.account_no) &&
        String(balance.id) !== String(actionData?.balanceId),
    );
    if (isDuplicate) {
      newErrors.account_no = t("openingBalance.duplicateAccount");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handelSave = async () => {
    if (!validation()) return;
    const payload = {
      ...(actionData.mode === "EDIT" && { id: actionData?.balanceId }),
      fin_year_id: periodId,
      tenant_id: tenantId,
      branch_id: branchSelected?.value,
      company_id: companySelected?.value,
      account_no: formData.account_no,
      cr: formData.cr,
      de: formData.de,
    };
    if (actionData?.mode === "ADD") {
      await addBalance(payload);
    } else if (actionData?.mode === "EDIT") {
      await editBalance(payload);
    }
    dispatch({ type: "reset" });
    setIsOpen(false);
  };
  return (
    <MwModal onShow={isOpen} setOnShow={setIsOpen}>
      <div className="flex flex-col h-full w-full bg-white rounded-lg shadow-lg font-normal">
        <div className="p-4 border-b bg-slate-100">
          <h2 className="font-normal text-[#261759] text-lg">
            {actionData?.mode === "ADD"
              ? t("openingBalance.add")
              : actionData?.mode === "EDIT"
                ? t("openingBalance.edit")
                : ""}
          </h2>
        </div>
        <div className="p-6 space-y-3 overflow-y-auto bg-slate-100">
          <div className="grid grid-cols-4 gap-4">
            <div className="mt-2 col-span-2">
              <SelectorTree
                data={accounts}
                disabled={isEditMode}
                onManualNumberChange={(number) => {
                  dispatch({ type: "account_no", value: number });
                  resolveAccountMeta(number);
                  setErrors((prev) => ({ ...prev, account_no: "" }));
                }}
                allowManualNumberInput={true}
                allowedAccountTypes={["secondary"]}
                withInlineSuggestions={true}
                headerTitle={t("accounts.account")}
                value={formData.account_no}
                label={formData.account_label}
                placeholderName={t("accounts.account")}
                placeholderNumber={t("accounts.accountNumber")}
                onSelect={(account) => {
                  dispatch({ type: "account_no", value: account.account_no });
                  resolveAccountMeta(
                    account.account_no,
                    account?.raw || account,
                  );
                  setErrors((prev) => ({ ...prev, account_no: "" }));
                }}
                invalid={Boolean(errors.account_no)}
                invalidMsg={errors.account_no}
                blockedAccountNumbers={blockedAccountNumbers}
              />
            </div>
            <MwInputText
              label={t("openingBalance.debit")}
              placeholder={t("openingBalance.debit")}
              value={formData.de}
              min={0}
              disabled={isCreditNature}
              inputType="number"
              onChange={(e) => {
                dispatch({ type: "de", value: e.target.value });
                dispatch({ type: "cr", value: "" });
                setErrors((prev) => ({ ...prev, de: "" }));
              }}
              invalid={errors.de}
              invalidMsg={errors.de}
            />
            <MwInputText
              label={t("openingBalance.credit")}
              placeholder={t("openingBalance.credit")}
              value={formData.cr}
              min={0}
              inputType="number"
              disabled={isDebitNature}
              onChange={(e) => {
                dispatch({ type: "cr", value: e.target.value });
                dispatch({ type: "de", value: "" });
                setErrors((prev) => ({ ...prev, cr: "" }));
              }}
              invalid={errors.cr}
              invalidMsg={errors.cr}
            />
          </div>
        </div>

        <div className="flex gap-2 bg-slate-200 p-4 rounded-b border-t border-slate-400 justify-end sticky bottom-0">
          <MwButton
            type="secondary"
            classNameCustom="!font-normal"
            onClick={handleClose}
          >
            {t("common.cancel")}
          </MwButton>
          <MwButton
            type="primary"
            classNameCustom="!font-normal"
            onClick={handelSave}
          >
            {loading ? (
              <MwSpinnerButton />
            ) : actionData?.mode === "ADD" ? (
              t("common.save")
            ) : actionData?.mode === "EDIT" ? (
              t("common.update")
            ) : (
              ""
            )}
          </MwButton>
        </div>
      </div>
    </MwModal>
  );
};
